'use client';

export type Language = 'en' | 'pt' | 'es';

export interface LanguageOption {
  code: Language;
  label: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
  flag: string;
  locale: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', nativeName: 'English', dir: 'ltr', flag: '🇺🇸', locale: 'en-US' },
  {
    code: 'pt',
    label: 'Portuguese',
    nativeName: 'Português',
    dir: 'ltr',
    flag: '🇧🇷',
    locale: 'pt-BR',
  },
  { code: 'es', label: 'Spanish', nativeName: 'Español', dir: 'ltr', flag: '🇲🇽', locale: 'es-MX' },
];

export const DEFAULT_LANGUAGE: Language = 'en';
export const LANGUAGE_STORAGE_KEY = 'aimaker_language';

export function getLanguageDirection(lang: Language): 'ltr' | 'rtl' {
  return SUPPORTED_LANGUAGES.find((l) => l.code === lang)?.dir ?? 'ltr';
}

/**
 * Map browser locale tags to a supported UI language.
 * Only uses navigator language tags — never country, project, or session data.
 */
export function detectBrowserLanguage(): Language {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }
  try {
    const candidates = [
      navigator.language,
      ...((navigator.languages as readonly string[] | undefined) ?? []),
    ];
    for (const raw of candidates) {
      if (!raw || typeof raw !== 'string') continue;
      const base = raw.toLowerCase().split('-')[0];
      if (base === 'en' || base === 'pt' || base === 'es') return base;
    }
  } catch {
    // ignore
  }
  return DEFAULT_LANGUAGE;
}

export function getStoredLanguage(): Language {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  } catch {
    // ignore
  }
  // No saved preference → supported browser locale, else English
  return detectBrowserLanguage();
}

export function setStoredLanguage(lang: Language): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    // Also persist in sessionStorage as backup
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}

/**
 * Returns a system instruction string to prepend to AI prompts so that
 * all generated content is in the user's selected language.
 */
export function getAILanguageInstruction(lang: Language): string {
  switch (lang) {
    case 'pt':
      return (
        'CRITICAL LANGUAGE RULE: Write your ENTIRE response in Brazilian Portuguese (Português do Brasil). ' +
        'Every title, description, list item, JSON string value, slogan, and sentence MUST be in Portuguese. '+ 'Do NOT use English except for unavoidable brand/product trademarks. '+ 'If the schema keys are in English, keep the keys in English but write all values in Portuguese.\n\n'
      );
    case 'es':
      return (
        'CRITICAL LANGUAGE RULE: Write your ENTIRE response in Spanish (Español). ' +
        'Every title, description, list item, JSON string value, slogan, and sentence MUST be in Spanish. '+ 'Do NOT use English except for unavoidable brand/product trademarks. '+ 'If the schema keys are in English, keep the keys in English but write all values in Spanish.\n\n'
      );
    default:
      return (
        'CRITICAL LANGUAGE RULE: Write your ENTIRE response in English. ' + 'Every title, description, list item, JSON string value, and sentence MUST be in English.\n\n'
      );
  }
}

/** Short closing reminder appended to user prompts after schema examples. */
export function getAILanguageReminder(lang: Language): string {
  switch (lang) {
    case 'pt':
      return '\n\nFINAL REMINDER: All JSON string values and prose must be in Brazilian Portuguese.';
    case 'es':
      return '\n\nFINAL REMINDER: All JSON string values and prose must be in Spanish.';
    default:
      return '\n\nFINAL REMINDER: All JSON string values and prose must be in English.';
  }
}

// Translation dictionaries — imported per language
import en from '@/locales/en.json';
import pt from '@/locales/pt.json';
import es from '@/locales/es.json';
import legalEn from '@/locales/legal-en.json';
import legalPt from '@/locales/legal-pt.json';
import legalEs from '@/locales/legal-es.json';
import templatesEn from '@/locales/pages/templates.en.json';
import templatesPt from '@/locales/pages/templates.pt.json';
import templatesEs from '@/locales/pages/templates.es.json';
import blogEn from '@/locales/pages/blog.en.json';
import blogPt from '@/locales/pages/blog.pt.json';
import blogEs from '@/locales/pages/blog.es.json';
import careersEn from '@/locales/pages/careers.en.json';
import careersPt from '@/locales/pages/careers.pt.json';
import careersEs from '@/locales/pages/careers.es.json';
import learnCenterEn from '@/locales/pages/learnCenter.en.json';
import learnCenterPt from '@/locales/pages/learnCenter.pt.json';
import learnCenterEs from '@/locales/pages/learnCenter.es.json';

type PageLocales = {
  templatesPage: typeof templatesEn;
  blogPage: typeof blogEn;
  careersPage: typeof careersEn;
  learnCenterPage: typeof learnCenterEn;
  legalPages: typeof legalEn;
};

function mergeLocale<T extends Record<string, unknown>>(
  base: T,
  pages: Omit<PageLocales, 'legalPages'>,
  legalPages: typeof legalEn
): T & PageLocales {
  return { ...base, ...pages, legalPages };
}

const translations: Record<Language, any> = {
  en: mergeLocale(
    en,
    {
      templatesPage: templatesEn,
      blogPage: blogEn,
      careersPage: careersEn,
      learnCenterPage: learnCenterEn,
    },
    legalEn
  ),
  pt: mergeLocale(
    pt,
    {
      templatesPage: templatesPt,
      blogPage: blogPt,
      careersPage: careersPt,
      learnCenterPage: learnCenterPt,
    },
    legalPt
  ),
  es: mergeLocale(
    es,
    {
      templatesPage: templatesEs,
      blogPage: blogEs,
      careersPage: careersEs,
      learnCenterPage: learnCenterEs,
    },
    legalEs
  ),
};

export type TranslationKeys = typeof en & PageLocales;

/**
 * Dev-mode missing translation logger.
 * Logs missing keys to console in development so they can be added.
 */
const loggedMissingKeys = new Set<string>();

export function logMissingTranslation(lang: Language, page: string, key: string): void {
  if (process.env.NODE_ENV !== 'production') {
    const id = `${lang}:${page}:${key}`;
    if (!loggedMissingKeys.has(id)) {
      loggedMissingKeys.add(id);
      console.warn(`[Missing Translation]\n  language: ${lang}\n  page: ${page}\n  key: ${key}`);
    }
  }
}

function createDevTranslationProxy<T extends object>(obj: T, lang: Language, path = ''): T {
  if (process.env.NODE_ENV === 'production') return obj;
  return new Proxy(obj, {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol') return Reflect.get(target, prop, receiver);
      const key = path ? `${path}.${String(prop)}` : String(prop);
      const value = Reflect.get(target, prop, receiver);
      if (value === undefined) {
        logMissingTranslation(lang, 'locale', key);
        return undefined;
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return createDevTranslationProxy(value as object, lang, key);
      }
      return value;
    },
  }) as T;
}

export function getTranslations(lang: Language): TranslationKeys {
  const dict = translations[lang] ?? translations['en'];
  return createDevTranslationProxy(dict, lang);
}

/**
 * Sample/demo project names translated per language.
 * These are application-generated names — NOT user content.
 */
export const SAMPLE_PROJECT_NAMES: Record<Language, Record<string, string>> = {
  en: {
    bakery: 'Bakery Business',
    barbershop: 'Barbershop Business',
    coffeeShop: 'Coffee Shop',
    childrensBook: "Children's Book",
    digitalCourse: 'Digital Course',
    fitnessCoaching: 'Fitness Coaching',
    clothingBrand: 'Clothing Brand',
    petGrooming: 'Pet Grooming Service',
    onlineStore: 'Online Store',
    restaurant: 'Restaurant Business',
  },
  pt: {
    bakery: 'Padaria',
    barbershop: 'Barbearia',
    coffeeShop: 'Cafeteria',
    childrensBook: 'Livro Infantil',
    digitalCourse: 'Curso Digital',
    fitnessCoaching: 'Coaching Fitness',
    clothingBrand: 'Marca de Roupas',
    petGrooming: 'Pet Shop',
    onlineStore: 'Loja Online',
    restaurant: 'Restaurante',
  },
  es: {
    bakery: 'Panadería',
    barbershop: 'Barbería',
    coffeeShop: 'Cafetería',
    childrensBook: 'Libro Infantil',
    digitalCourse: 'Curso Digital',
    fitnessCoaching: 'Coaching Fitness',
    clothingBrand: 'Marca de Ropa',
    petGrooming: 'Peluquería Canina',
    onlineStore: 'Tienda Online',
    restaurant: 'Restaurante',
  },
};
